// src/components/CharacterForm.jsx
import React, { useState, useEffect } from "react";

const CharacterForm = ({ initialData = {}, onSubmit }) => {
  const [formData, setFormData] = useState({
    characterId: "",
    characterName: "",
    category: "Entertainment",
    country: "",
    description: "",
    prompt: "",
    imageUrl: "",
    isTrending: false,
    ...initialData,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.characterName.trim()) {
      newErrors.characterName = "Character name is required";
    }
    
    if (!formData.characterId.trim()) {
      newErrors.characterId = "Character ID is required";
    }
    
    if (!formData.prompt.trim()) {
      newErrors.prompt = "Prompt is required for AI character";
    }
    
    if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
      newErrors.imageUrl = "Please enter a valid image URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;  
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        createdAt: formData.createdAt || new Date(),
      });
      // Reset form on success if creating new character
      if (!initialData?.characterId) {
        setFormData({
          characterId: "",
          characterName: "",
          category: "Entertainment",
          country: "",
          description: "",
          prompt: "",
          imageUrl: "",
          isTrending: false,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {initialData?.characterId ? "Edit Character" : "Create New Character"}
          </h1>
          <p className="text-gray-600">Design your AI character with personality and purpose</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <h2 className="text-white text-lg font-semibold">
              Character Details
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              Fill in the details to bring your character to life
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Character ID and Name Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Character ID
                </label>
                <input
                  type="text"
                  name="characterId"
                  value={formData.characterId}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.characterId ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200'
                  }`}
                  placeholder="e.g., character_001"
                />
                {errors.characterId && (
                  <p className="text-red-500 text-sm mt-1">{errors.characterId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Character Name
                </label>
                <input
                  type="text"
                  name="characterName"
                  value={formData.characterName}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.characterName ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200'
                  }`}
                  placeholder="e.g., AI Assistant"
                />
                {errors.characterName && (
                  <p className="text-red-500 text-sm mt-1">{errors.characterName}</p>
                )}
              </div>
            </div>

            {/* Category and Country Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="e.g., Entertainment, Education, Assistant"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country/Origin
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="e.g., USA, Japan, Virtual"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Character Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Describe the character's personality, background, and characteristics..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
              ></textarea>
            </div>

            {/* AI Prompt - Highlighted Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">AI Character Prompt</h3>
                  <p className="text-sm text-gray-600">This is the core instruction for your AI character</p>
                </div>
              </div>
              <textarea
                name="prompt"
                value={formData.prompt}
                onChange={handleChange}
                rows="5"
                placeholder="Enter detailed instructions for how this character should behave, respond, and interact. Be specific about personality traits, knowledge areas, and communication style..."
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 ${
                  errors.prompt ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200'
                }`}
              ></textarea>
              {errors.prompt && (
                <p className="text-red-500 text-sm mt-1">{errors.prompt}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Tip: Be specific about personality, knowledge areas, response style, and any limitations
              </p>
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Character Image URL
              </label>
              <input
                type="url"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleChange}
                placeholder="https://example.com/character-image.jpg"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.imageUrl ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200'
                }`}
              />
              {errors.imageUrl && (
                <p className="text-red-500 text-sm mt-1">{errors.imageUrl}</p>
              )}
              {formData.imageUrl && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">Character Preview:</p>
                  <div className="relative inline-block">
                    <img
                      src={formData.imageUrl}
                      alt="Character Preview"
                      className="w-32 h-32 rounded-xl object-cover shadow-lg border-2 border-gray-100"
                    />
                    <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      Live
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Trending Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="isTrending"
                  checked={formData.isTrending}
                  onChange={handleChange}
                  className="w-5 h-5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-blue-500 cursor-pointer transition-all duration-200 hover:border-blue-400"
                />
                <div>
                  <label className="text-gray-900 font-medium">Featured Character</label>
                  <p className="text-sm text-gray-500">Make this character appear in trending lists</p>
                </div>
              </div>
              {formData.isTrending && (
                <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Trending
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  initialData?.characterId ? "Update Character" : "Create Character"
                )}
              </button>
              
              {!initialData?.characterId && (
                <button
                  type="button"
                  onClick={() => setFormData({
                    characterId: "",
                    characterName: "",
                    category: "Entertainment",
                    country: "",
                    description: "",
                    prompt: "",
                    imageUrl: "",
                    isTrending: false,
                  })}
                  className="px-6 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                >
                  Clear Form
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CharacterForm;
